import React from "react"
import ProtectedLayout from "layouts/Protected"
import { useTranslation } from "react-i18next"

import * as PartnerApi from "features/Partner/api/PartnerApi"

import { useAuth, useAlert, useTheme } from "hooks"
import AccountIcon from "components/Elements/Icons/Account"
import { fetchPartner } from "./hooks/fetchPartner"
import { prepareDataToPut } from "./hooks/prepareDataToPut"
import { BC_OPERATOR, SELLER_PERMISSION } from "utils/constants"

import {
	MandateForm,
	AdditionalInformationForm,
	ManagerForm,
	AddressForm,
	StructureForm,
	BilingForm,
} from "./Partials"
import { ErrorResponse } from "models"

export const PartnerInfo = () => {
	const [partner, setPartner] = React.useState(null)
	const [updatedPartner, setUpdatedPartner] = React.useState(null)
	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)

	const { entity, isConnectedFromSupport, fetchEntity, user } = useAuth()
	const { t } = useTranslation()
	const { alert } = useAlert()
	const { currentTheme } = useTheme()

	React.useEffect(() => {
		if (entity) fetchPartner(entity, setPartner)
	}, [entity])

	const isBCPartner = entity?.data_operators?.find(
		(oneOperator) => oneOperator.operator === BC_OPERATOR
	)
	const isBCCurrentTheme = currentTheme?.id_api === BC_OPERATOR
	const isProductionEnvironment = process.env.REACT_APP_MODE === "production" // Ne pas afficher le bloc Mandat en production sans le raccordement avec sage
	const isSellerPermission = user?.permission === SELLER_PERMISSION

	const editPartner = async (updatedPartner) => {
		try {
			// hide error
			setShowLocalErrors(false)

			const updatedPartnerObject = prepareDataToPut({
				isUpdate: true,
				partner: updatedPartner,
				isPartner: true,
			})

			const { getError } = await PartnerApi.updateEntity(updatedPartnerObject)
			if (getError()) throw getError()
			alert("success", t("edit_success", { ns: "partner" }))
		} catch (error) {
			console.error(error)
			const erroMsg = (error as ErrorResponse)?.message?.toString()
			if (erroMsg) alert("error", erroMsg)
		} finally {
			fetchEntity(entity.entity_id)
		}
	}

	const componentCommonProps = {
		partner: partner,
		editCallback: editPartner,
		updatedPartner: updatedPartner,
		setUpdatedPartner: setUpdatedPartner,
		errors: errors,
		setErrors: setErrors,
		showLocalErrors: showLocalErrors,
		setShowLocalErrors: setShowLocalErrors,
	}

	return (
		<ProtectedLayout className="px-7 py-7">
			{!partner && (
				<div className="flex justify-center items-center py-12">
					<div className="c-spinner"></div>
				</div>
			)}

			{partner && (
				<div className="l-container">
					<div className="c-section-title mb-5">
						<div className="c-section-title__content justify-between">
							<div className="flex items-center justify-start w-full">
								<AccountIcon size={35} style={{ marginBottom: 5 }} />
								<h1 className="c-section-title__title c-h2 text-primary font-medium ml-5">
									{t("store.title", { ns: "partner" })}
								</h1>
							</div>
						</div>
					</div>

					<div className="text-right mb-5 text-xs">
						{t("store.declarator_number", { ns: "partner" })} : {partner.entity_id}
					</div>

					<StructureForm {...componentCommonProps} isEdit={true} isViewMode={true} />

					<AddressForm {...componentCommonProps} isEdit={true} isViewMode={true} />

					{
						// The block is only visible if the partner is BC and if the current theme is BC
						isBCPartner && isBCCurrentTheme && (
							<>
								<BilingForm
									{...componentCommonProps}
									isEdit={true}
									isViewMode={isSellerPermission}
									isAdminEditing={false}
								/>

								{!isProductionEnvironment && (
									<MandateForm
										{...componentCommonProps}
										isEdit={true}
										isViewMode={isConnectedFromSupport || isSellerPermission}
										isAdminViewing={false}
									/>
								)}
							</>
						)
					}

					<ManagerForm
						{...componentCommonProps}
						isEdit={true}
						isViewMode={true}
						refresh={fetchEntity}
					/>

					<AdditionalInformationForm
						{...componentCommonProps}
						isEdit={true}
						isViewMode={isSellerPermission}
						refresh={fetchEntity}
						isAdminEditing={false}
					/>
				</div>
			)}
		</ProtectedLayout>
	)
}
